"use client";

import { useEffect, useRef } from "react";
import { useRouter } from "next/navigation";

type RedirectMethod = "push" | "replace";

interface UseRouteAccessGuardOptions {
  isLoading: boolean;
  isChecking?: boolean;
  canAccess: boolean;
  redirectTo: string;
  redirectMethod?: RedirectMethod;
  onDeny?: () => void;
}

interface UseRouteAccessGuardResult {
  isChecking: boolean;
  canRender: boolean;
}

/**
 * Standard route-access guard for client pages.
 * Ensures redirects only happen after auth/permission state is fully resolved.
 */
export function useRouteAccessGuard({
  isLoading,
  isChecking,
  canAccess,
  redirectTo,
  redirectMethod = "push",
  onDeny,
}: UseRouteAccessGuardOptions): UseRouteAccessGuardResult {
  const router = useRouter();
  const checking = isChecking ?? isLoading;
  const shouldRedirect = !checking && !canAccess;
  const hasDeniedRef = useRef(false);

  useEffect(() => {
    if (!shouldRedirect) {
      hasDeniedRef.current = false;
      return;
    }
    if (!hasDeniedRef.current) {
      onDeny?.();
      hasDeniedRef.current = true;
    }
    if (redirectMethod === "replace") {
      router.replace(redirectTo);
      return;
    }
    router.push(redirectTo);
  }, [onDeny, redirectMethod, redirectTo, router, shouldRedirect]);

  return {
    isChecking: checking,
    canRender: !checking && canAccess,
  };
}
